import {
  ECustomerTypeView,
  ECustomerUserPermissionType,
  IUser
} from '../../store/ducks/user/types';
import {
  customersAuthorizationApi,
  ECustomerAuthorizationEndpoints
} from '../api';
import { EGerdauService } from '../EGerdauService';

export class AccessManagementService extends EGerdauService {
  async getCustomerUsers(customerId: string): Promise<IUser[]> {
    const headers = super.getHeaders();

    const userResponse = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.USERS}${ECustomerAuthorizationEndpoints.USER_CUSTOMER_MANAGEMENTE}?customer=${customerId}`,
      { headers }
    );

    const users: IUser[] = userResponse.data.users || [];
    return users;
  }

  async updateUserPermission(
    user: IUser,
    customerId: string,
    customerUserPermissionType: ECustomerUserPermissionType,
    updateType: 'add' | 'remove'
  ): Promise<IUser> {
    if (updateType === 'add') {
      return this.addUserPermission(
        user,
        customerId,
        customerUserPermissionType
      );
    } else {
      return this.removeUserPermission(
        user,
        customerId,
        customerUserPermissionType
      );
    }
  }

  private async addUserPermission(
    user: IUser,
    customerId: string,
    customerUserPermissionType: ECustomerUserPermissionType
  ): Promise<IUser> {
    const headers = super.getHeaders();

    const roles = {
      [customerUserPermissionType]: [customerId]
    };

    // Acesso a aba Finanças também dá permissão de ver preços
    if (customerUserPermissionType === ECustomerUserPermissionType.FINANCIAL) {
      roles[ECustomerUserPermissionType.VIEW_PRICES] = [customerId];
    }

    const response = await customersAuthorizationApi.patch(
      `${ECustomerAuthorizationEndpoints.USERS}${ECustomerAuthorizationEndpoints.USER_CUSTOMER_MANAGEMENTE}/${user.email}`,
      { user: { roles } },
      { headers }
    );

    user.roles = response.data.users[0].roles;

    return user;
  }

  private async removeUserPermission(
    user: IUser,
    customerId: string,
    customerUserPermissionType: ECustomerUserPermissionType
  ): Promise<IUser> {
    const headers = super.getHeaders();

    let newUser = await customersAuthorizationApi.delete(
      `${ECustomerAuthorizationEndpoints.USERS}${ECustomerAuthorizationEndpoints.USER_CUSTOMER_MANAGEMENTE}/${user.email}/roles/${customerUserPermissionType}/customers/${customerId}`,
      { headers }
    );

    // Remover acesso de Finanças, também remove a permissão de ver preços
    if (customerUserPermissionType === ECustomerUserPermissionType.FINANCIAL) {
      newUser = await customersAuthorizationApi.delete(
        `${ECustomerAuthorizationEndpoints.USERS}${ECustomerAuthorizationEndpoints.USER_CUSTOMER_MANAGEMENTE}/${user.email}/roles/${ECustomerUserPermissionType.VIEW_PRICES}/customers/${customerId}`,
        { headers }
      );
    }

    user.roles = newUser.data.users[0].roles;
    return user;
  }

  async createCustomerUser(
    customerId: string,
    name: string,
    email: string,
    admin: boolean,
    fabricationPermission: boolean,

    ordersViewPermission: boolean,
    ordersViewPrice: boolean,
    financialViewPermission: boolean,
    orderInputPermission: boolean,

    coreTeam: boolean
  ) {
    const headers = super.getHeaders();

    const response = await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.USERS,
      {
        users: [
          {
            name,
            email,
            roles: {
              adminCustomers: admin ? [customerId] : [],
              financialCustomers: financialViewPermission ? [customerId] : [],
              ordersViewPriceCustomers: ordersViewPrice ? [customerId] : [],
              ordersViewCustomers: ordersViewPermission ? [customerId] : [],
              ordersInputCustomers: orderInputPermission ? [customerId] : [],

              fabricationViewCustomers: fabricationPermission
                ? [customerId]
                : []
            },
            coreTeam,

            searchCustomerType: fabricationPermission
              ? ECustomerTypeView.FABRICATION_RECEIVER
              : ECustomerTypeView.EMITTER
          }
        ]
      },
      { headers }
    );

    const result = response.data.result;

    if (result.user_exists) {
      const userEmail = result.user_exists.email;
      const roles = {
        adminCustomers: admin ? [customerId] : undefined,
        financialCustomers: financialViewPermission ? [customerId] : undefined,
        ordersViewPriceCustomers: ordersViewPrice ? [customerId] : undefined,
        ordersViewCustomers: ordersViewPermission ? [customerId] : undefined,
        ordersInputCustomers: orderInputPermission ? [customerId] : undefined,

        fabricationViewCustomers: fabricationPermission
          ? [customerId]
          : undefined
      };
      await customersAuthorizationApi.patch(
        `${ECustomerAuthorizationEndpoints.USERS}${ECustomerAuthorizationEndpoints.USER_CUSTOMER_MANAGEMENTE}/${userEmail}`,
        { user: { roles } },
        { headers }
      );
    }
  }

  async removeUserFromCustomer(user: IUser, customerId: string): Promise<void> {
    const headers = super.getHeaders();
    await customersAuthorizationApi.delete(
      `${ECustomerAuthorizationEndpoints.USERS}${ECustomerAuthorizationEndpoints.USER_CUSTOMER_MANAGEMENTE}/${user.email}/roles/all/customers/${customerId}`,
      { headers }
    );
  }
}
